import { defineStore } from "pinia";
import { hasValue, keysTyped } from "~/helpers/common-helpers";

interface SearchEntryBase {
  type: SearchType;
  search: Record<string, string>;
  fields: Record<string, any>;
}

interface SearchEntryPortfolio extends SearchEntryBase {
  type: "ASSET_MANAGEMENT_PORTFOLIO";
  search: { id: string; name: string };
}

interface SearchEntryProperty extends SearchEntryBase {
  type: "ASSET_MANAGEMENT_PROPERTY";
  search: { id: string; name: string };
}

interface SearchEntryTenancy extends SearchEntryBase {
  type: "ASSET_MANAGEMENT_TENANCY";
  search: { id: string; name: string };
}

interface SearchEntryCanvasLayout extends SearchEntryBase {
  type: "CANVAS_LAYOUT";
  search: { id: string; name: string };
}

export type SearchEntry = SearchEntryPortfolio | SearchEntryProperty | SearchEntryTenancy | SearchEntryCanvasLayout;

export type SearchType = SearchEntry["type"];

export const useSearchStore = defineStore("search", () => {
  const ampli = useAmplitude();

  const searchTerm = ref("");

  const results = ref<SearchEntry[]>([]);

  const selection = ref<number>(-1);

  const selectedItem = ref<SearchEntry>();

  const showingSelected = computed(() => searchTerm.value === selectedItem.value?.search.name);

  const updateSelection = (val: number) => {
    const newSelection = selection.value + val;
    if (newSelection < -1) {
      selection.value = results.value.length - 1;
    } else if (newSelection > results.value.length - 1) {
      selection.value = -1;
    } else {
      selection.value = newSelection;
    }
  };

  const selectItem = () => {
    const item = results.value[selection.value];

    if (!item) return;

    selectedItem.value = item;

    let params;
    let name;

    if (item.type === "ASSET_MANAGEMENT_PORTFOLIO") {
      name = "portfolio-portfolioId";
      params = { portfolioId: `${item.search.id}` };
    } else if (item.type === "ASSET_MANAGEMENT_PROPERTY") {
      name = "portfolio-property-propertyId";
      params = { propertyId: `${item.search.id}` };
    } else if (item.type === "ASSET_MANAGEMENT_TENANCY") {
      name = "portfolio-tenancy-tenancyId";
      params = { tenancyId: `${item.search.id}` };
    } else if (item.type === "CANVAS_LAYOUT") {
      name = "reports-layoutId";
      params = { layoutId: `${item.search.id}` };
    }

    ampli.searchSelect({
      searchTerm: searchTerm.value,
      name: item.search.name,
      id: item.search.id,
      type: item.type,
    });

    searchTerm.value = item.search.name;

    if (!name || !params) return;

    navigateTo({ name, params });
  };

  return {
    searchTerm,
    results,
    selectedItem,
    selection,
    showingSelected,
    updateSelection,
    selectItem,
  };
});
